import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Check, Loader2, Tag, X } from "lucide-react";
import { api, rupee } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { cart, load, loaded } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({
    full_name: user?.name || "", phone: "", email: user?.email || "",
    line1: "", city: "", state: "", pincode: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = Object.values(form).every((v) => v.trim());

  const discount = coupon ? coupon.discount : 0;
  const shippingTotal = quote ? quote.total_shipping : 0;
  const payable = Math.max(0, cart.total - discount) + shippingTotal;

  // Fetch a live shipping quote whenever the buyer's city/state (or cart) changes.
  useEffect(() => {
    if (!form.city.trim() || !form.state.trim() || cart.items.length === 0) { setQuote(null); return; }
    const t = setTimeout(() => {
      api.post("/shipping/quote", { city: form.city, state: form.state })
        .then(({ data }) => setQuote(data))
        .catch(() => setQuote(null));
    }, 500);
    return () => clearTimeout(t);
  }, [form.city, form.state, cart.items.length]);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput });
      setCoupon(data);
      toast.success(`Coupon ${data.code} applied!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid coupon");
    }
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const placeOrder = async () => {
    if (!valid) { toast.error("Please fill all delivery details"); return; }
    setPlacing(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load the payment gateway. Check your connection.");
      const { data } = await api.post("/payments/create-order", form, { params: coupon ? { coupon_code: coupon.code } : {} });
      const rz = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "handmadegiftkart",
        description: "Handmade gifts order",
        order_id: data.order_id,
        prefill: { name: data.name, email: data.email, contact: data.contact },
        theme: { color: "#8C3D2B" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setDone(true);
            await load();
            toast.success("Payment successful — order confirmed!");
            setTimeout(() => nav("/account"), 1400);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
            setPlacing(false);
          }
        },
        modal: { ondismiss: () => { setPlacing(false); toast("Payment cancelled"); } },
      });
      rz.on("payment.failed", (r) => { toast.error(r.error?.description || "Payment failed"); setPlacing(false); });
      rz.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Checkout failed");
      setPlacing(false);
    }
  };

  useEffect(() => {
    if (loaded && !done && cart.items.length === 0) nav("/cart");
  }, [loaded, done, cart.items.length, nav]);

  if (!loaded) return <div className="min-h-screen grid place-items-center text-mute">Loading…</div>;
  if (cart.items.length === 0 && !done) return null;

  if (done) {
    return (
      <div className="pt-40 pb-24 min-h-screen">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md mx-auto px-5 text-center">
          <div className="w-20 h-20 rounded-full bg-forest grid place-items-center mx-auto mb-6"><Check className="w-9 h-9 text-sand" /></div>
          <h1 className="font-serif text-4xl tracking-tight">Order confirmed!</h1>
          <p className="text-mute mt-3">Your makers have been notified and will begin crafting. Taking you to your orders…</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-24 sm:pt-28 pb-24 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className="overline text-clay mb-3">Secure checkout</div>
        <h1 className="font-serif text-4xl sm:text-6xl tracking-tighter">Where should love arrive?</h1>

        <div className="grid lg:grid-cols-[1fr_360px] gap-12 mt-10">
          <div className="bg-surface rounded-sm p-6 sm:p-8 border border-line">
            <h2 className="font-serif text-2xl mb-6">Delivery details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { k: "full_name", label: "Full name" },
                { k: "phone", label: "Phone" },
                { k: "email", label: "Email", type: "email", span: true },
                { k: "line1", label: "Address", span: true },
                { k: "city", label: "City" },
                { k: "state", label: "State" },
                { k: "pincode", label: "Pincode" },
              ].map(({ k, label, span, ...rest }) => (
                <div key={k} className={span ? "sm:col-span-2" : ""}>
                  <label className="overline text-mute block mb-1.5">{label}</label>
                  <input value={form[k]} onChange={set(k)} {...rest}
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid={`checkout-${k}`} />
                </div>
              ))}
            </div>

            <div className="mt-8 border-t border-line pt-6">
              <h2 className="font-serif text-2xl mb-3">Payment</h2>
              <div className="flex items-center gap-2 text-sm bg-sand rounded-sm px-4 py-3 border border-line">
                <Lock className="w-4 h-4 text-forest" /> Secure prepaid via UPI / Card / Netbanking
                <span className="ml-auto text-xs text-mute">Razorpay</span>
              </div>
              <p className="text-xs text-mute mt-2">Payments are processed securely by Razorpay. You&apos;ll complete payment in a secure window.</p>
            </div>
          </div>

          <div className="lg:sticky lg:top-24 h-fit bg-surface rounded-sm p-6 border border-line">
            <h2 className="font-serif text-2xl">Your order</h2>
            <div className="space-y-3 mt-5">
              {cart.items.map((it) => (
                <div key={it.product_id} className="flex gap-3 text-sm">
                  <img src={it.image} alt="" className="w-12 h-14 rounded-sm object-cover" />
                  <div className="flex-1">
                    <div className="leading-tight">{it.title}</div>
                    <div className="text-mute text-xs">Qty {it.qty} · {it.seller_name}</div>
                  </div>
                  <span>{rupee(it.price * it.qty)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-line">
              {coupon ? (
                <div className="flex items-center justify-between bg-forest/5 border border-forest/20 rounded-sm px-3 py-2" data-testid="applied-coupon">
                  <span className="flex items-center gap-2 text-sm text-forest"><Tag className="w-4 h-4" /> {coupon.code} · −{rupee(coupon.discount)}</span>
                  <button onClick={() => { setCoupon(null); setCouponInput(""); }} data-testid="remove-coupon-btn"><X className="w-4 h-4 text-mute" /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Coupon code"
                    className="flex-1 bg-sand border border-line rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="coupon-input" />
                  <button onClick={applyCoupon} className="rounded-sm bg-forest text-sand px-4 py-2 text-sm hover:bg-clay transition-colors" data-testid="apply-coupon-btn">Apply</button>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-line space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-mute">Subtotal</span><span>{rupee(cart.total)}</span></div>
              {discount > 0 && <div className="flex justify-between text-sm text-forest"><span>Coupon discount</span><span>−{rupee(discount)}</span></div>}
              <div className="flex justify-between text-sm">
                <span className="text-mute">Shipping</span>
                <span data-testid="checkout-shipping">
                  {!quote ? <span className="text-mute text-xs">Enter city & state</span>
                    : shippingTotal === 0 ? <span className="text-forest">FREE</span>
                    : rupee(shippingTotal)}
                </span>
              </div>
              {quote && quote.sellers.length > 1 && (
                <div className="text-xs text-mute pl-1 space-y-0.5" data-testid="checkout-shipping-breakup">
                  {quote.sellers.map((s) => (
                    <div key={s.seller_id} className="flex justify-between">
                      <span className="truncate pr-2">{s.seller_name}</span>
                      <span>{s.shipping === 0 ? "Free" : rupee(s.shipping)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between font-semibold text-lg pt-1"><span>Total</span><span data-testid="checkout-total">{rupee(payable)}</span></div>
            </div>
            <button onClick={placeOrder} disabled={placing}
              className="w-full rounded-full bg-clay text-sand py-3.5 mt-5 text-sm hover:bg-forest transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="place-order-btn">
              {placing ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : `Pay ${rupee(payable)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
